import React, {useState, useEffect, useRef} from "react";
import {createPortal} from "react-dom";
import useFetchSearchResults from "../../Hooks/Content/Search/useFetchSearchResults";

// Assets & Components
import IconSearchMagnifier from "../../Assets/SVGs/Icons/UI/SearchMagnifier";
import InputField from "../Inputs/InputField";
import IconClose from "../../Assets/SVGs/Icons/UI/Close";
import LandscapeContentItem from "../Layouts/Content/LandscapeContentItem";
import IconPenWriting from "../../Assets/SVGs/Icons/Writing/PenWriting";
import IconTV from "../../Assets/SVGs/Icons/Devices/TV";
import IconFilmStrip from "../../Assets/SVGs/Icons/Content/FilmStrip";
import IconSlate from "../../Assets/SVGs/Icons/Content/Slate";
import IconArrowBack from "../../Assets/SVGs/Icons/UI/ArrowBack";
import ErrorMessage from "../Text/ErrorMessage";
import SuccessMessage from "../Text/SuccessMessage";

// Models & Enums
import ICarouselContentDTO from "../../Models/DTOs/ICarouselContentDTO";
import {AuthUser} from "../../Models/DTOs/AuthUser";
import {ContentType} from "../../Models/Enums/ContentType";
import WarningMessage from "../Text/WarningMessage";
import IconListCheckmarks from "../../Assets/SVGs/Icons/List/ListCheckmarks";
import IconListBoxes from "../../Assets/SVGs/Icons/List/ListBoxes";
import IconCamera from "../../Assets/SVGs/Icons/Content/Camera";
import IconCheckMark from "../../Assets/SVGs/Icons/YesOrNo/CheckMark";
import IconCloseAlt from "../../Assets/SVGs/Icons/UI/CloseAlternate";
import IconAddPlus from "../../Assets/SVGs/Icons/YesOrNo/AddPlus";

// Context
import { usePopup, PopupType } from "../../Context/PopupContext";

interface SearchFiltersState {
    showFilms: boolean;
    showEpisodes: boolean;
    showSeries: boolean;
    showArticles: boolean;
}

interface SearchBarProps {
    userData: AuthUser | Record<string, never> | null;
    authTokenExists: boolean;
    className: string;
}

const mapToCarouselDTO = (item: any, fallbackType: ContentType): ICarouselContentDTO => {
    let determinedType = fallbackType;

    if (item.ContentType !== undefined || item.contentType !== undefined) {
        determinedType = item.ContentType ?? item.contentType;
    } else if (fallbackType === ContentType.Film && (item.Episode > 0 || item.episode > 0)) {
        determinedType = ContentType.Episode;
    }

    let resolvedThumbnail = "";
    switch (determinedType) {
        case ContentType.Series:
            resolvedThumbnail = item.HeaderImg || item.headerImg || item.headerimg || "";
            break;
        case ContentType.Article:
            resolvedThumbnail = item.Header || item.header || "";
            break;
        case ContentType.Film:
        case ContentType.Episode:
        default:
            resolvedThumbnail = item.Thumbnail || item.thumbnail || "";
            break;
    }

    const finalThumb = resolvedThumbnail.replace('/compressed/', '/small/');

    return {
        Id: item.Id || item.id || 0,
        Title: item.Title || item.title || "",
        Text: item.Description || item.description || item.Text || item.text || "",
        Season: item.Season || item.season || 0,
        Episode: item.Episode || item.episode || 0,
        CategoryId: item.CategoryId || item.categoryId || 0,
        Thumbnail: finalThumb,
        PortraitThumbnail: item.PortraitThumbnail || item.portraitThumbnail || "",
        BackgroundImage: item.BackgroundImage || item.backgroundImage || "",
        AccessType: item.AccessType || item.accessType || 0,
        ContentType: determinedType,
        Created: item.Created || item.created || "",
        ReleaseDate: item.ReleaseDate || item.releasedate || item.datetime || "",
        ReleaseDateEarlyAccess: item.ReleaseDateEarlyAccess || item.releaseDateEarlyAccess || "",
        IsOriginal: !!(item.IsOriginal || item.isOriginal),
        IsFreeView: !!(item.IsFreeView || item.isFreeView),
        IsFreeViewApp: !!(item.IsFreeViewApp || item.isFreeViewApp),
        IsVisible: !!(item.IsVisible || item.isVisible),
        IsFavourite: !!(item.IsFavourite || item.isFavourite),
        Rating: item.Rating || item.rating || 0,
        IsBookable: !!(item.IsBookable || item.isBookable),
        IsClickeable: item.IsClickeable !== undefined ? item.IsClickeable : true,
        VideoLinkHLS: item.VideoLinkHLS || item.videoLinkHLS || "",
        VideoLinkDash: item.VideoLinkDash || item.videoLinkDash || "",
        TrailerLinkHLS: item.TrailerLinkHLS || item.trailerLinkHLS || "",
        TrailerLinkDash: item.TrailerLinkDash || item.trailerLinkDash || "",
        Link: item.Link || item.link || "",
        OptionalLabel: item.OptionalLabel || item.optionalLabel || "",
        Authors: item.Authors || item.authors || []
    };
};

export default function SearchBar({userData, authTokenExists, className}: SearchBarProps) {
    const { openPopup, closePopup, closePopupIfType, isPopupOpen } = usePopup();
    const isActive = isPopupOpen(PopupType.SEARCH);
    const [inputText, setInputText] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

    // NEW: Pagination State
    const itemsPerPage = 40;
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageInputValue, setPageInputValue] = useState<string>("1");

    const contentWrapperRef = useRef<HTMLDivElement>(null);

    const [filters, setFilters] = useState<SearchFiltersState>({
        showFilms: true,
        showEpisodes: true,
        showSeries: true,
        showArticles: true
    });

    // Reset pagination to Page 1 if the user changes their search term or toggles a filter
    useEffect(() => {
        setCurrentPage(1);
        setPageInputValue("1");
    }, [debouncedSearchTerm, filters.showFilms, filters.showEpisodes, filters.showSeries, filters.showArticles]);

    const handleClose = () => {
        closePopup();
        setInputText("");
    };

    useEffect(() => {
        return () => closePopupIfType(PopupType.SEARCH);
    }, [closePopupIfType]);

    useEffect(() => {
        if (isActive) {
            document.body.classList.add('disable-scroll');
        } else {
            document.body.classList.remove('disable-scroll');
        }
        return () => {
            document.body.classList.remove('disable-scroll');
        };
    }, [isActive]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isActive) return;

            if (
                contentWrapperRef.current &&
                !contentWrapperRef.current.contains(event.target as Node)
            ) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isActive]);

    useEffect(() => {
        if (isActive && contentWrapperRef.current) {
            setTimeout(() => {
                const inputElement = contentWrapperRef.current?.querySelector('input[name="search"]') as HTMLInputElement;
                if (inputElement) {
                    inputElement.focus();
                }
            }, 100);
        }
    }, [isActive]);

    useEffect(() => {
        const timerId = setTimeout(() => setDebouncedSearchTerm(inputText), 500);
        return () => clearTimeout(timerId);
    }, [inputText]);

    const activeTypes: string[] = [];
    if (filters.showFilms || filters.showEpisodes) activeTypes.push("video");
    if (filters.showSeries) activeTypes.push("series");
    if (filters.showArticles) activeTypes.push("article");

    const {results, isLoading, error} = useFetchSearchResults(
        debouncedSearchTerm,
        activeTypes,
        !filters.showEpisodes,
        itemsPerPage,
        (currentPage - 1) * itemsPerPage, // Calculate Skip based on Current Page
        false
    );

    // Calculate Total Pages safely
    const totalPages = Math.max(1, Math.ceil((results.total || 0) / itemsPerPage));

    const handleFilterChange = (key: keyof SearchFiltersState) => {
        if (filters[key] === true) {
            const activeCount =
                (filters.showFilms ? 1 : 0) +
                (filters.showEpisodes ? 1 : 0) +
                (filters.showSeries ? 1 : 0) +
                (filters.showArticles ? 1 : 0);

            if (activeCount === 1) return;
        }
        setFilters(prev => ({...prev, [key]: !prev[key]}));
    };

    // --- Pagination Handlers ---
    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
            setPageInputValue(String(currentPage - 1));
            // Optional: Scroll to top of results container here
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
            setPageInputValue(String(currentPage + 1));
            // Optional: Scroll to top of results container here
        }
    };

    const applyPageInput = () => {
        let parsed = parseInt(pageInputValue, 10);
        if (isNaN(parsed) || parsed < 1) parsed = 1;
        if (parsed > totalPages) parsed = totalPages;

        setCurrentPage(parsed);
        setPageInputValue(String(parsed));
    };

    const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applyPageInput();
        }
    };

    const filteredItems = results.items?.filter((item: any) => {
        if (!filters.showFilms && item.DataType === "Film") return false;
        return true;
    });

    return (
        <div className={`search-bar-container ${className} ${authTokenExists ? 'is-authenticated' : 'is-not-authenticated'}`}>
            <div className="search-trigger" onClick={() => openPopup(PopupType.SEARCH)}>
                <IconSearchMagnifier/>
                <p>
                    Search
                </p>
            </div>

            {createPortal(
                <div className={`search-portal-overlay ${isActive ? "is-active" : "is-not-active"}`}>
                    <button className="close-btn" onClick={handleClose}>
                        <IconClose/>
                    </button>

                    <div className="overlay__content-wrapper" ref={contentWrapperRef}>
                        <div className="overlay__search-header">
                            <IconSearchMagnifier className="icon__search-magnifier icon__search"/>

                            <InputField
                                type="text"
                                name="search"
                                value={inputText}
                                label=""
                                onChange={(e) => setInputText(e.target.value)}
                            />

                            {inputText && (
                                <div className="icon__close icon__search" onClick={() => setInputText("")}>
                                    <IconClose/>
                                </div>
                            )}

                            <div className="overlay__messages">
                                {!isLoading && !error && !debouncedSearchTerm && (
                                    <SuccessMessage successMessage="Start typing to search the archive" />
                                )}

                                {!isLoading && !error && results.total !== 0 && (
                                    <SuccessMessage successMessage={`${results.total} results found`} />
                                )}

                                {isLoading && !error && (
                                    <WarningMessage message="Searching..." />
                                )}

                                {error && <ErrorMessage errorMessage={error}/>}

                                {!isLoading && !error && debouncedSearchTerm && results.total === 0 && <ErrorMessage errorMessage="No items found."/>}
                            </div>
                        </div>

                        <div className="overlay__filters">
                            <p>I'm looking for...</p>

                            <div className="overlay__filters-wrapper">
                                <div className="overlay__filters-inner">
                                    <div
                                        className={`filters__single-data-type ${filters.showFilms ? 'is-checked' : 'is-not-checked'}`}
                                        onClick={() => handleFilterChange('showFilms')}>
                                        {filters.showFilms ? <IconCheckMark/> : <IconAddPlus />}
                                        <label>Films</label>
                                    </div>

                                    <div
                                        className={`filters__single-data-type ${filters.showEpisodes ? 'is-checked' : 'is-not-checked'}`}
                                        onClick={() => handleFilterChange('showEpisodes')}>
                                        {filters.showEpisodes ? <IconCheckMark/> : <IconAddPlus />}
                                        <label>Episodes</label>
                                    </div>

                                    <div
                                        className={`filters__single-data-type ${filters.showSeries ? 'is-checked' : 'is-not-checked'}`}
                                        onClick={() => handleFilterChange('showSeries')}>
                                        {filters.showSeries ? <IconCheckMark/> : <IconAddPlus />}
                                        <label>Series</label>
                                    </div>

                                    <div
                                        className={`filters__single-data-type ${filters.showArticles ? 'is-checked' : 'is-not-checked'}`}
                                        onClick={() => handleFilterChange('showArticles')}>
                                        {filters.showArticles ? <IconCheckMark/> : <IconAddPlus />}
                                        <label>Articles</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={`overlay__search-inner ${(results.total || 0) > itemsPerPage ? 'has-pagination' : 'has-single-page'}`}>
                            <div className="overlay__search-results">
                                {error && <ErrorMessage errorMessage={error}/>}

                                {!isLoading && !error && debouncedSearchTerm && (
                                    <div className="results-container">
                                        <div className="results-grid">
                                            {filteredItems?.map((item: any, i) => {
                                                let fallbackType = ContentType.Film;
                                                if (item.DataType === "Series") fallbackType = ContentType.Series;
                                                if (item.DataType === "Article") fallbackType = ContentType.Article;
                                                if (item.DataType === "Episode") fallbackType = ContentType.Episode;
                                                if (item.DataType === "Film") fallbackType = ContentType.Film;

                                                let typeIcon;
                                                if (item.DataType === "Series") typeIcon = <IconTV/>;
                                                if (item.DataType === "Article") typeIcon = <IconPenWriting/>;
                                                if (item.DataType === "Episode") typeIcon = <IconListBoxes/>;
                                                if (item.DataType === "Film") typeIcon = <IconCamera/>;

                                                return (
                                                    <div className="grid__item" onClick={handleClose} key={i}>
                                                        <div className="content-label">
                                                            {typeIcon} <label>{item.DataType}</label>
                                                        </div>

                                                        <LandscapeContentItem
                                                            key={item.Id || item.id}
                                                            item={mapToCarouselDTO(item, fallbackType)}
                                                            userData={userData}
                                                            authTokenExists={authTokenExists}
                                                            isLoading={false}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {(results.total || 0) > itemsPerPage && (
                            <div className="search-pagination">
                                <button
                                    className="pagination-btn pagination-prev"
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1 || isLoading}
                                >
                                    <IconArrowBack />
                                </button>

                                <div className="pagination-info">
                                    <span>Page</span>
                                    <input
                                        type="text"
                                        className="pagination-input"
                                        value={pageInputValue}
                                        onChange={(e) => setPageInputValue(e.target.value)}
                                        onBlur={applyPageInput}
                                        onKeyDown={handlePageInputKeyDown}
                                        disabled={isLoading}
                                    />
                                    <span>of {totalPages}</span>
                                </div>

                                <button
                                    className="pagination-btn pagination-next"
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages || isLoading}
                                >
                                    <IconArrowBack />
                                </button>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
